import { DAY } from '@lib/utils/time';
import { decodeJwt } from 'jose';
import { userStore } from '../User';

type PreviewToken = {
  token: string;
  expiresAt: number;
};

const _PREVIEW_LOCAL_STORAGE_KEY = 'libraryPreviewTokens';

class LibraryPreview {
  _previewTokens: Record<string, PreviewToken> = {};

  _previewTokenPromises: Record<string, Promise<string>> = {};

  constructor() {
    this.retrievePreviewTokensFromLocalStorage();
  }

  protected retrievePreviewTokensFromLocalStorage() {
    const storedTokens = localStorage.getItem(_PREVIEW_LOCAL_STORAGE_KEY);
    if (!storedTokens) {
      return;
    }

    try {
      const parsedTokens = JSON.parse(storedTokens) as Record<string, string>;
      if (!parsedTokens || typeof parsedTokens !== 'object' || Array.isArray(parsedTokens)) {
        throw new Error('Invalid token data structure');
      }

      const validTokens: Record<string, string> = {};
      for (const [libraryId, token] of Object.entries(parsedTokens)) {
        if (!libraryId || !token || typeof token !== 'string') {
          console.warn(`Skipping invalid preview token entry for library ${libraryId}`);
          continue;
        }

        try {
          this._previewTokens[libraryId] = { token, expiresAt: this.getPreviewTokenExpiry(token) };
          validTokens[libraryId] = token;
        } catch (error) {
          // If token is corrupted/invalid, skip it
          console.warn(`Invalid preview token for library ${libraryId}, skipping:`, error);
        }
      }

      // Update localStorage with only valid tokens
      if (Object.keys(validTokens).length === 0) {
        localStorage.removeItem(_PREVIEW_LOCAL_STORAGE_KEY);
      } else if (Object.keys(validTokens).length !== Object.keys(parsedTokens).length) {
        // Some tokens were invalid, update storage with only valid ones
        localStorage.setItem(_PREVIEW_LOCAL_STORAGE_KEY, JSON.stringify(validTokens));
      }
    } catch (error) {
      // If JSON parsing fails or data is corrupted, clear the corrupted data
      console.warn(
        'Failed to parse preview tokens from localStorage (corrupted data), clearing:',
        error,
      );
      try {
        localStorage.removeItem(_PREVIEW_LOCAL_STORAGE_KEY);
      } catch (removeError) {
        // If removal fails, try to set it to empty object
        console.error('Failed to remove corrupted preview tokens from localStorage:', removeError);
        try {
          localStorage.setItem(_PREVIEW_LOCAL_STORAGE_KEY, '{}');
        } catch (setError) {
          console.error('Failed to clear corrupted preview tokens from localStorage:', setError);
        }
      }
    }
  }

  protected storePreviewTokensInLocalStorage() {
    const tokensToStore: Record<string, string> = {};
    for (const [libraryId, token] of Object.entries(this._previewTokens)) {
      tokensToStore[libraryId] = token.token;
    }
    localStorage.setItem(_PREVIEW_LOCAL_STORAGE_KEY, JSON.stringify(tokensToStore));
  }

  deinit() {
    this._previewTokens = {};
    this._previewTokenPromises = {};
  }

  /**
   * Synchronously return a cached token if one exists and hasn't expired.
   * Returns null on cache miss — does NOT fetch or proactively refresh.
   * Uses a relaxed expiry check (token just needs to be valid now) unlike
   * getValidPreviewToken which proactively refreshes tokens expiring within a day.
   */
  getCachedPreviewToken(libraryId: string): string | null {
    const existingToken = this._previewTokens[libraryId];
    if (existingToken && existingToken.expiresAt > Date.now()) {
      return existingToken.token;
    }
    return null;
  }

  async getValidPreviewToken(libraryId: string): Promise<string> {
    const existingToken = this._previewTokens[libraryId];
    const now = Date.now();

    // Check if we have a valid token that's not about to expire
    if (existingToken && existingToken.expiresAt > now + DAY) {
      return existingToken.token;
    }

    // If a refresh is already in progress, await it
    if (libraryId in this._previewTokenPromises) {
      const token = await this._previewTokenPromises[libraryId];
      // Promise completed - token is guaranteed to be a string here
      // If a new refresh started during our await, we still return the token we got
      return token;
    }

    // At this point, either:
    // 1. No token exists (existingToken is undefined), OR
    // 2. Token expires within 24 hours (we already returned if it expires later)
    // So we should refresh the token (proactively if expiring soon, or reactively if expired)
    this._previewTokenPromises[libraryId] = this.fetchPreviewToken(libraryId)
      .then((token) => {
        this._previewTokens[libraryId] = { token, expiresAt: this.getPreviewTokenExpiry(token) };
        this.storePreviewTokensInLocalStorage();
        delete this._previewTokenPromises[libraryId]; // Clear promise
        return token;
      })
      .catch((error) => {
        // Clear the promise on error so we can retry
        delete this._previewTokenPromises[libraryId];

        // If fetch failed but we have a valid token that hasn't expired yet, use it
        const existingToken = this._previewTokens[libraryId];
        if (existingToken && existingToken.expiresAt > Date.now()) {
          console.warn(
            `Preview token refresh failed for library ${libraryId}, using existing valid token:`,
            error,
          );
          return existingToken.token;
        }

        // No valid fallback, re-throw
        throw error;
      });

    return this._previewTokenPromises[libraryId];
  }

  /**
   * Discard a library's cached token and fetch a fresh one.
   *
   * For the one case the cache cannot detect on its own: the server rejected a
   * token whose `exp` has not passed yet (revoked, or signed with a rotated key),
   * so `getValidPreviewToken` would hand back the same rejected token. Call this
   * from a 401 handler; call `getValidPreviewToken` everywhere else.
   */
  async refreshPreviewToken(libraryId: string): Promise<string> {
    delete this._previewTokens[libraryId];
    this.storePreviewTokensInLocalStorage();
    return this.getValidPreviewToken(libraryId);
  }

  protected getPreviewTokenExpiry(token: string) {
    try {
      const decoded = decodeJwt(token);
      if (!decoded.exp) {
        throw new Error('Preview token does not have an expiration date');
      }
      return decoded.exp * 1000;
    } catch (error) {
      // If token is invalid/corrupted, throw a more descriptive error
      throw new Error(
        `Invalid preview token: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  protected async fetchPreviewToken(libraryId: string): Promise<string> {
    const { previewToken } = await userStore.apiWithAuth<{ previewToken: string }>(
      `/library/${libraryId}/preview`,
    );
    return previewToken;
  }
}

// singleton, as we don't need multiple instances of users
// also simplifies our life with api calls from other stores
export const libraryPreview = new LibraryPreview();
